import { useEffect, useState } from 'react';
import { Link, Routes, Route, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  Breadcrumbs,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import ReportsIcon from '@mui/icons-material/Assessment';
import BillingIcon from '@mui/icons-material/CreditCard';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AdminNavRow from '../../components/AdminNavRow';
import { UserManagement, TenantSettings } from './';
import ComplianceReport from './ComplianceReport';
import CpeHistoryImport from '../CpeHistoryImport';
import BillingManagement from '../../components/BillingManagement';
import { useUserContext } from '../../hooks/useUserContext';
import tenantService, { type TenantStats } from '../../services/tenantService';
import addonAssignmentService from '../../services/addonAssignmentService';
import complianceReportService from '../../services/complianceReportService';
import billingService, { type Subscription } from '../../services/billingService';
import { getStandingCounts, type StandingCounts } from '../../components/compliance/firmRollup';

const SUB_PAGE_LABELS: Record<string, string> = {
  users: 'User Management',
  billing: 'Billing & Subscription',
  settings: 'Settings',
  'compliance-reports': 'Team Reporting',
  'user-management': 'User Management',
  'import-credits': 'Import credits',
};

function formatGlanceDate(iso?: string): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

interface GlanceRowProps {
  to?: string;
  label?: string;
  value?: number | string;
  /** Status-dot color token (e.g. 'status.atRisk.dot'). Mirrors the report
      legend's dot vocabulary exactly (always full strength, even at 0);
      numbers stay neutral and zero rows de-emphasize via muted ink. */
  dotColor?: string;
  children?: React.ReactNode;
}

function GlanceRow({ to, label, value, dotColor, children }: GlanceRowProps) {
  const isNumber = typeof value === 'number';
  const isMissing = value == null || value === '–';
  return (
    <Box
      component={to ? Link : 'div'}
      {...(to ? { to } : {})}
      sx={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 2,
        px: 1,
        mx: -1,
        py: 0.75,
        textDecoration: 'none',
        borderRadius: 1,
        ...(to && {
          transition: 'background-color 0.15s ease',
          '&:hover': { bgcolor: 'tint.neutralSoft' },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: -2,
          },
        }),
      }}
    >
      {children ?? (
        <>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {dotColor && (
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: dotColor,
                  mr: 1,
                }}
              />
            )}
            {label}
          </Typography>
          <Typography
            sx={{
              fontSize: isNumber ? '1.0625rem' : '0.875rem',
              // One weight for every numeral: zeros de-emphasize via ink
              // (text.secondary) and the faded dot, never via weight, so the
              // number column stays visually uniform.
              fontWeight: isNumber ? 600 : isMissing ? 400 : 500,
              color: isMissing
                ? 'text.disabled'
                : isNumber && value === 0
                  ? 'text.secondary'
                  : 'text.primary',
              fontVariantNumeric: 'tabular-nums',
              textAlign: 'right',
            }}
          >
            {value ?? '–'}
          </Typography>
        </>
      )}
    </Box>
  );
}

export default function TenantAdminLayout() {
  const { backendProfile } = useUserContext();
  const location = useLocation();
  const tenantId = backendProfile?.tenantId || '';
  const [stats, setStats] = useState<TenantStats | null>(null);
  const [standing, setStanding] = useState<StandingCounts | null>(null);
  const [standingError, setStandingError] = useState(false);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  const basePath = '/app/tenant-admin';
  const subSegment = location.pathname.replace(basePath, '').replace(/^\//, '').split('/')[0];
  const isSubPage = Boolean(subSegment);
  const subPageLabel = SUB_PAGE_LABELS[subSegment] ?? subSegment;

  useEffect(() => {
    async function fetchStats() {
      if (!tenantId || isSubPage) {
        return;
      }

      setStatsLoading(true);
      setStatsError(null);
      setStandingError(false);

      const [statsResult, reportResult, requestsResult, subscriptionResult] = await Promise.allSettled([
        tenantService.getTenantStats(tenantId, 'ytd'),
        complianceReportService.getComplianceReport(tenantId),
        addonAssignmentService.getAccessRequests(tenantId, 'pending'),
        billingService.getSubscription(tenantId),
      ]);

      if (statsResult.status === 'fulfilled') {
        setStats(statsResult.value);
      } else {
        const reason = statsResult.reason;
        setStatsError(reason instanceof Error ? reason.message : 'Failed to load summary stats');
      }

      if (reportResult.status === 'fulfilled') {
        setStanding(getStandingCounts(reportResult.value.users, new Date()));
      } else {
        setStandingError(true);
      }

      if (requestsResult.status === 'fulfilled') {
        setPendingRequests(requestsResult.value.length);
      }

      if (subscriptionResult.status === 'fulfilled') {
        setSubscription(subscriptionResult.value);
      }

      setStatsLoading(false);
    }

    fetchStats();
  }, [tenantId, isSubPage, reloadTick]);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumb — only on sub-pages */}
      {isSubPage && (
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Typography
            component={Link}
            to={basePath}
            sx={{ fontSize: 14, color: 'text.secondary', textDecoration: 'none', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}
          >
            Tenant Administration
          </Typography>
          <Typography sx={{ fontSize: 14, color: 'text.primary', fontWeight: 500 }}>
            {subPageLabel}
          </Typography>
        </Breadcrumbs>
      )}

      {/* Header (billing and compliance-reports render their own) */}
      {subSegment !== 'billing' && subSegment !== 'compliance-reports' && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {isSubPage ? subPageLabel : 'Tenant Administration'}
          </Typography>
          {subSegment === 'settings' && (
            <Typography variant="body1" color="text.secondary">
              The name and logo your team sees in the app.
            </Typography>
          )}
        </Box>
      )}

        <Routes>
          <Route path="users" element={<UserManagement />} />
          <Route path="billing" element={<BillingManagement tenantId={tenantId} />} />
          <Route path="settings" element={<TenantSettings />} />
          <Route path="compliance-reports" element={<ComplianceReport />} />
          <Route
            path="import-credits"
            element={
              // The profile may still be loading; without a tenant id the page would import for the admin personally.
              tenantId ? (
                <CpeHistoryImport key={tenantId} tenantId={tenantId} backTo={{ to: basePath, label: 'Tenant Administration' }} hideHeader />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={24} />
                </Box>
              )
            }
          />
          <Route
            index
            element={
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 3fr) minmax(0, 2fr)' },
                  columnGap: { md: 6, lg: 10 },
                  rowGap: 5,
                  alignItems: 'start',
                }}
              >
                {/* Navigation */}
                <Box>
                  <Box component="nav" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    <AdminNavRow
                      to={`${basePath}/users`}
                      icon={PeopleIcon}
                      title="Users"
                      desc={
                        pendingRequests > 0
                          ? `User accounts · ${pendingRequests} pending access ${pendingRequests === 1 ? 'request' : 'requests'}`
                          : 'User accounts for your organization'
                      }
                      badgeCount={pendingRequests}
                    />
                    <AdminNavRow
                      to={`${basePath}/compliance-reports`}
                      icon={ReportsIcon}
                      title="Team Reporting"
                      desc="Compliance standing and CPE history for your team"
                    />
                    <AdminNavRow
                      to={`${basePath}/import-credits`}
                      icon={UploadFileIcon}
                      title="Import CPE credits"
                      desc="Load your team's past CPE from a spreadsheet or another tool"
                    />
                    <AdminNavRow
                      to={`${basePath}/billing`}
                      icon={BillingIcon}
                      title="Billing & Subscription"
                      desc="Subscription and billing details"
                    />
                    <AdminNavRow
                      to={`${basePath}/settings`}
                      icon={SettingsIcon}
                      title="Settings"
                      desc="Organization configuration"
                    />
                  </Box>
                </Box>

                {/* At a glance */}
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'surface.raised',
                    p: 2.5,
                  }}
                >
                  <Typography
                    variant="overline"
                    component="h2"
                    sx={{ display: 'block', color: 'text.secondary', lineHeight: 2 }}
                  >
                    At a glance
                  </Typography>

                  {statsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        mt: 0.5,
                        '& > * + *': { borderTop: '1px solid', borderColor: 'divider', pt: 2, mt: 2 },
                      }}
                    >
                      {/* Compliance */}
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                          Compliance
                        </Typography>
                        {standingError || !standing ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">
                              Couldn't load compliance standing.
                            </Typography>
                            <Button size="small" onClick={() => setReloadTick((t) => t + 1)}>
                              Retry
                            </Button>
                          </Box>
                        ) : (
                          <>
                            {standing.attention + standing.atRisk === 0 ? (
                              <GlanceRow to={`${basePath}/compliance-reports`}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <CheckCircleIcon sx={{ fontSize: 18, color: 'status.completed.fg' }} />
                                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                    Everyone is on track with CPE
                                  </Typography>
                                </Box>
                              </GlanceRow>
                            ) : (
                              <>
                                <GlanceRow
                                  to={`${basePath}/compliance-reports?status=attention`}
                                  label="Needs attention"
                                  value={standing.attention}
                                  dotColor="status.attention.dot"
                                />
                                <GlanceRow
                                  to={`${basePath}/compliance-reports?status=at_risk`}
                                  label="At risk"
                                  value={standing.atRisk}
                                  dotColor="status.atRisk.dot"
                                />
                              </>
                            )}
                            {/* Calm standings, same vocabulary + colors as the
                                report's legend: blue = still earning, green = done. */}
                            <GlanceRow
                              to={`${basePath}/compliance-reports?status=in_progress`}
                              label="In progress"
                              value={standing.inProgress}
                              dotColor="status.onPace.dot"
                            />
                            <GlanceRow
                              to={`${basePath}/compliance-reports?status=concluded`}
                              label="Compliant"
                              value={standing.concluded}
                              dotColor="status.completed.dot"
                            />
                          </>
                        )}
                      </Box>

                      {/* Users */}
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                          Users
                        </Typography>
                        {statsError ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">
                              Couldn't load user stats.
                            </Typography>
                            <Button size="small" onClick={() => setReloadTick((t) => t + 1)}>
                              Retry
                            </Button>
                          </Box>
                        ) : (
                          <>
                            <GlanceRow to={`${basePath}/users`} label="Total users" value={stats?.totalUsers} />
                            <GlanceRow
                              to={`${basePath}/users`}
                              label="Pending access requests"
                              value={pendingRequests}
                            />
                            <GlanceRow label="Courses completed (YTD)" value={stats?.completedCourses} />
                          </>
                        )}
                      </Box>

                      {/* Billing */}
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                          Billing
                        </Typography>
                        <GlanceRow
                          to={`${basePath}/billing`}
                          label="Plan"
                          value={subscription?.planName ?? '–'}
                        />
                        <GlanceRow
                          to={`${basePath}/billing`}
                          label="Next renewal"
                          value={formatGlanceDate(subscription?.nextBillingAt ?? subscription?.currentTermEnd) ?? '–'}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            }
          />
        </Routes>
    </Container>
  );
}

